import * as Sentry from '@sentry/browser'
import type {
  EmployeeSchemaLatest,
  ReportManualSchemaLatest,
  ReportNodeSchemaLatest,
  ReportSchemaLatest,
  TemplateMediaSchemaLatest,
} from '~/adapter/indexedDB/types'
import type { ReadonlyImage } from '~/domain/report/model/image/image'
import { Image } from '~/domain/report/model/image/image'
import type { Node } from '~/domain/report/model/report/node/node'
import type { Hint } from '~/domain/report/model/report/node/questionNode/hint'
import type { QuestionType } from '~/domain/report/model/report/node/questionNode/question'
import type { QuestionNodeByType } from '~/domain/report/model/report/node/questionNode/questionNode'
import { ReportMode } from '~/domain/report/model/report/reportMode'
import type { ToDefined } from '~/utils/types/defined'
import { convertEmployees } from './employee/convertEmployee'
import { ConvertToOfflineError } from './error'
import { convertInformationDocumentQuestionNode } from './questionNode/convertInformationDocument'
import { convertMemo } from './questionNode/convertMemo'
import { convertMultipleChoiceQuestionNode } from './questionNode/convertMultipleChoice'
import { convertResultImage } from './questionNode/convertResultImgae'
import { convertSignature } from './questionNode/convertSignature'
import { convertToHide } from './questionNode/convertToHide'
import type { State } from '../../../../../components/domain/EditReport/store/store'

type NodeSchema = ToDefined<ReportNodeSchemaLatest['data']['nodes'][0]>

type SaveOperationType = 'submit' | 'saveInProgress'

export const convertToOffline = async (
  state: State,
  companyId: number,
  saveOperationType: SaveOperationType,
  placeNodeId?: string,
  // Wi-Fiオフ等のフォールバック保存時に呼び出し元から渡す記録者スナップショット。
  // new-report 系かつ reportAssignee 未設定のときのみ注入する。
  // オフラインモード初期化時に既にスナップショットが設定されている場合は上書きしない。
  recorder?: { uuid: string; name: string },
): Promise<
  | {
      offlineReport: ReportSchemaLatest
      offlineReportNode: ReportNodeSchemaLatest
      offlineReportManual: ReportManualSchemaLatest
      offlineEmployee: EmployeeSchemaLatest
    }
  | Error
> => {
  try {
    if (!state.reportMode) {
      throw new ConvertToOfflineError('reportMode is required')
    }

    // offlineReport がある状態 = 必ずオフライン保存を行っているため、 UUID が付与されている
    const uuid = state.offlineReport
      ? state.offlineReport.uuid
      : ReportMode.getReportUuid(state.reportMode)

    const nodes: ReportNodeSchemaLatest['data']['nodes'] = {}
    for (const node of Object.values(state.report.nodes)) {
      if (node) {
        nodes[node.id] = await convertNode(node)
      }
    }

    // Wi-Fiオフのフォールバック保存では、作成時点の記録者（UUID + 名前）を
    // reportAssignee として IndexedDB に書き込む。後から別ユーザーがアップロードしても
    // 「誰が作ったか」を保持するためのスナップショット。
    //
    // 以下の場合は何もしない（state.reportMode をそのまま使う）:
    //   - editReport: 記録者はサーバが既存レポートから引き継ぐためクライアント側では設定しない
    //   - reportAssignee 設定済み: オフラインモードの初期化時に既に書き込まれているため上書きしない
    //   - recorder が undefined: Device 記録者など、スナップショットが不要なケース
    const reportMode =
      state.reportMode.modeType !== 'editReport' &&
      !state.reportMode.reportAssignee &&
      recorder
        ? { ...state.reportMode, reportAssignee: recorder }
        : state.reportMode

    const offlineReport: ReportSchemaLatest = {
      uuid,
      placeNodeId,
      companyId,
      data: {
        saveOperationType,
        reportMode,
        // カイゼン（指摘）データを永続化する。アップロード時に buildReport へ渡す。
        improve: state.improve ?? undefined,
        linkedIndicatedIssues: state.linkedIndicatedIssues,
      },
    }

    const offlineReportNode: ReportNodeSchemaLatest = {
      uuid,
      companyId,
      data: {
        pageNodeIds: state.report.pageNodeIds,
        nodes,
      },
    }

    const offlineReportManual: ReportManualSchemaLatest = {
      uuid,
      placeNodeId,
      companyId,
      data: {
        manuals: state.report.manuals,
      },
    }

    const offlineEmployee = convertEmployees(
      state.report.employees,
      state.warnsInStartingReport.hasMoreEmployees,
      placeNodeId,
      companyId,
    )

    return {
      offlineReport,
      offlineReportNode,
      offlineReportManual,
      offlineEmployee,
    }
  } catch (e) {
    return processError(e)
  }
}

const processError = (e: unknown): Error => {
  if (e instanceof Error) {
    Sentry.captureException(e)
    return e
  } else {
    const error = new ConvertToOfflineError(
      'An unexpected error has occurred in converting store to request.',
      {
        unknownError: e,
      },
    )
    Sentry.captureException(error)
    return error
  }
}

const convertNode = async (node: Node): Promise<NodeSchema> => {
  switch (node.type) {
    case 'logic': {
      return node
    }
    case 'question': {
      switch (node.questionType) {
        case 'resultImage': {
          return convertResultImage(node)
        }
        case 'multipleChoice': {
          return convertMultipleChoiceQuestionNode(node)
        }
        case 'signature': {
          return convertSignature(node)
        }
        case 'informationDocument': {
          return convertInformationDocumentQuestionNode(node)
        }
        default: {
          return convertQuestionNodeWithMemo(node)
        }
      }
    }
    case 'section': {
      return node
    }
    case 'page': {
      return node
    }
    default:
      throw new ConvertToOfflineError(`Unexpected node type.`, { node })
  }
}

// questionNode の型に応じた処理を switch を使わずにうまく書く方法がないため、linter を抑止している
// 処理を煩雑にしないようにするため、この関数の責務は type に応じて呼び出す関数などを switch することだけに限定すること
// eslint-disable-next-line complexity
export const convertNodeWithMedia = async (
  node: Node,
): Promise<{
  media?: TemplateMediaSchemaLatest['media']['underlyingRemoteFile'][]
  node: NodeSchema
}> => {
  switch (node.type) {
    case 'logic': {
      return { node }
    }
    case 'question': {
      switch (node.questionType) {
        case 'resultImage': {
          return {
            media: extractMediaFromHints(node.question.hints),
            node: {
              ...node,
              answer: undefined,
              memo: await convertMemo(node.memo),
              hide: convertToHide(node),
            },
          }
        }
        case 'multipleChoice': {
          const media = node.question.images
            .filter(
              (image): image is ReadonlyImage => !Image.isLocalImage(image),
            )
            .map(image => {
              const { uuid, url } = Image.getRemoteResourceInfo(image)
              return {
                uuid,
                url,
              }
            })
          return {
            media: media.concat(...extractMediaFromHints(node.question.hints)),
            node: await convertMultipleChoiceQuestionNode(node),
          }
        }
        case 'signature': {
          return {
            media: extractMediaFromHints(node.question.hints),
            node: {
              ...node,
              answer: undefined,
              memo: await convertMemo(node.memo),
              hide: convertToHide(node),
            },
          }
        }
        case 'informationDocument': {
          const file =
            node.question.information &&
            Image.getRemoteResourceInfo(node.question.information)
          const media = file
            ? [
                {
                  uuid: file.uuid,
                  url: file.url,
                },
              ]
            : []
          return {
            media: media.concat(...extractMediaFromHints(node.question.hints)),
            node: await convertInformationDocumentQuestionNode(node),
          }
        }
        default: {
          return {
            media: extractMediaFromHints(node.question.hints),
            node: await convertQuestionNodeWithMemo(node),
          }
        }
      }
    }
    case 'section': {
      return { node }
    }
    case 'page': {
      return { node }
    }
    default:
      throw new ConvertToOfflineError(`Unexpected node type.`, { node })
  }
}

const convertQuestionNodeWithMemo = async <T extends QuestionType>(
  node: QuestionNodeByType<T>,
) => {
  return {
    ...node,
    memo: await convertMemo(node.memo),
    hide: convertToHide(node),
  }
}

const extractMediaFromHints = (
  hints: Hint[],
): TemplateMediaSchemaLatest['media']['underlyingRemoteFile'][] =>
  hints.flatMap(hint => {
    if (hint.type === 'localRo') {
      throw new ConvertToOfflineError(`Unexpected hint type.`, { hint })
    }
    return hint.blocks
      .filter(block => block.type === 'image')
      .map(imageBlock => ({
        ...imageBlock.data.file,
      }))
  })
